import * as React from 'react';
import { Link } from "react-router-dom";
import { useQuery } from '../../Components/Query';
import { Column, Table } from "../../Components/QueryTable";
import { Authorization, GlobalAuthorization } from "../../misc/components/Authorization";
import { useSecurityContext } from "../../Core/SecurityContext";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { MatchSortHeader } from '../../Components/MatchSortHeader';
import { WebPortalClient, Study } from 'webportalclient';
import { ErrorBoundary } from "../../errors/ErrorBoundary";
import { LocalizedDateTime, DateTimeAccuracy } from '../../misc/components/LocalizedDateTime';
import Condition from '../../misc/components/Condition';
import { SessionTypes } from '../../misc/api/ApiSessionTypes';
import { DateMatchSortHeader } from '../../Components/DateMatchSortHeader';
import { useWebPortalApiClient } from '../../misc/api/WebPortalTokenClient';
import { DeviceTypes } from '../../misc/api/apiTypes';

export const StudiesTable = () => {
    const { currentUserPermissions } = useSecurityContext();
    const { data, applyFilters } = useQuery();
    const client = useWebPortalApiClient();
    const [nameOptions, setNameOptions] = React.useState([]);
    const [protocolOptions, setProtocolOptions] = React.useState([]);

    React.useEffect(() => {

        const getFilterOptions = async () => {

            const studiesResult = await client.client.getStudies('name,protocol');
            const nameOptions = studiesResult.value.map(s => ({
                value: s.name,
                label: s.name,
            }));

            setNameOptions(nameOptions);

            const protocolOptions = studiesResult.value.map(s => ({
                value: s.protocol,
                label: s.protocol,
            }));

            setProtocolOptions(protocolOptions);
        };

        getFilterOptions();
    }, []);

    const handleClearFilters = () => applyFilters([]);

    const menuFormatter = (study: Study) => {
        return (
            <div className="text-center">
                <div className="btn-group" role="group">
                    <button type="button" className="btn btn-primary btn-xs dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                        <i className="fas fa-bars" aria-hidden="true" />
                    </button>
                    <ul className="dropdown-menu pull-right scrollable-menu" role="menu">
                        <Authorization
                            permissionIdsAny={[PermissionEnum.EditStudy]}
                            studyId={study.id}
                            currentPermissions={currentUserPermissions}
                        >
                            <li>
                                <Link to={`/studies/edit/${study.id}`}>Edit</Link>
                            </li>
                        </Authorization>
                        <li>
                            <Link to={`/visitschedule/${study.id}`}>Visit Schedule</Link>
                        </li>
                        <Condition test={() => study && study.sessionTypeIds.find(id => id === SessionTypes.Cough) !== undefined}>
                            <li>
                                <Link to={`/studies/coughsettings/${study.id}`}>Cough Settings</Link>
                            </li>
                        </Condition>
                        <Condition test={() => study && study.sessionTypeIds.find(id => id === SessionTypes.Fvc) !== undefined}>
                            <li>
                                <Link to={`/studies/fvcsettings/${study.id}`}>FVC Settings</Link>
                            </li>
                        </Condition>
                        <Condition test={() => study && study.sessionTypeIds.find(id => id === SessionTypes.Svc) !== undefined}>
                            <li>
                                <Link to={`/studies/svcsettings/${study.id}`}>SVC Settings</Link>
                            </li>
                        </Condition>
                        <Condition test={() => study && study.sessionTypeIds.find(id => id  === SessionTypes.Pcf) !== undefined}>
                            <li>
                                <Link to={`/studies/pcfsettings/${study.id}`}>PCF Settings</Link>
                            </li>
                        </Condition>
                        <Condition test={() => study && study.sessionTypeIds.find(id => id === SessionTypes.Dlco) !== undefined}>
                            <li>
                                <Link to={`/studies/dlcosettings/${study.id}`}>Dlco Settings</Link>
                            </li>
                        </Condition>
                        <Condition test={() => study && study.sessionTypeIds.find(id => id === SessionTypes.Ecg) !== undefined}>
                            <li>
                                <Link to={`/studies/ecgsettings/${study.id}`}>ECG Settings</Link>
                            </li>
                        </Condition>
                        <Condition test={() => study && study.deviceTypeIds.find(id => id === DeviceTypes.In2itiveEDiary || id === DeviceTypes.In2itiveP230EDiary) !== undefined}>
                            <li>
                                <Link to={`/studies/diarysettings/${study.id}`}>Diary Settings</Link>
                            </li>
                        </Condition>
                        <Condition test={() => study && study.sessionTypeIds.find(id => id === SessionTypes.LungVolumeHelium) !== undefined}>
                            <li>
                                <Link to={`/studies/lungvolumeheliumsettings/${study.id}`}>Lung Volume Helium Settings</Link>
                            </li>
                        </Condition>
                        <Condition test={() => study && study.sessionTypeIds.find(id => id === SessionTypes.LungVolumePleth) !== undefined}>
                            <li>
                                <Link to={`/studies/lungvolumeplethsettings/${study.id}`}>Lung Volume Pleth Settings</Link>
                            </li>
                        </Condition>
                        <Condition test={() => study && study.sessionTypeIds.find(id => id === SessionTypes.PulseOximetry) !== undefined}>
                            <li>
                                <Link to={`/studies/pulseoximetrysettings/${study.id}`}>Pulse Oximetry Settings</Link>
                            </li>
                        </Condition>
                        <Authorization
                            permissionIdsAny={[PermissionEnum.ViewPopulationGroups]}
                            studyId={study.id}
                            currentPermissions={currentUserPermissions}
                        >
                            <li>
                                <Link to={`/studies/populationgroups/${study.id}`}>Population Groups</Link>
                            </li>
                        </Authorization>
                        <Authorization
                            permissionIdsAny={[PermissionEnum.CustomiseStudy]}
                            studyId={study.id}
                            currentPermissions={currentUserPermissions}
                        >
                            <li>
                                <Link to={`/studies/customisation/${study.id}`}>Customise</Link>
                            </li>
                        </Authorization>
                        <li>
                            <Link to={`/studies/dcfsettings/${study.id}`}>DCF Settings</Link>
                        </li>
                        <Condition test={() => study && study.sessionTypeIds.find(id => id === SessionTypes.Mip) !== undefined}>
                            <li>
                                <Link to={`/studies/mipsettings/${study.id}`}>MIP Settings</Link>
                            </li>
                        </Condition>
                        <Condition test={() => study && study.sessionTypeIds.find(id => id === SessionTypes.Mep) !== undefined}>
                            <li>
                                <Link to={`/studies/mepsettings/${study.id}`}>MEP Settings</Link>
                            </li>
                        </Condition>
                        <Condition test={() => study && study.sessionTypeIds.find(id => id === SessionTypes.Snip) !== undefined}>
                            <li>
                                <Link to={`/studies/snipsettings/${study.id}`}>SNIP Settings</Link>
                            </li>
                        </Condition>
                    </ul>
                </div>
            </div>
        );
    };

    return (
        <ErrorBoundary>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Studies</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <GlobalAuthorization currentPermissions={currentUserPermissions} permissionIdsAny={[PermissionEnum.CreateStudy]}>
                            <Link id="createNew" className="btn btn-primary" to="/studies/create">
                                Create New
                            </Link>&nbsp;
                        </GlobalAuthorization>
                        <button className="btn btn-primary" onClick={handleClearFilters}>Clear All Filters</button>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <div id="StudiesTable" className="table-responsive" data-automationid="StudiesTable">
                                <Table data={data}>
                                    <Column field="name" header={<MatchSortHeader title="Name" field="name" options={nameOptions} sortField="name" />} formatter={n => n} />
                                    <Column field="protocol" header={<MatchSortHeader title="Protocol Number" field="protocol" options={protocolOptions} sortField="protocol" />} formatter={n => n} />
                                    <Column field="modified" header={<DateMatchSortHeader title="Modified At" field="modified" sortField="modified" />} formatter={n => <LocalizedDateTime time={n} accuracy={DateTimeAccuracy.Second} />} />
                                    <Column field="" formatter={menuFormatter} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
};
